import React, { useState, useEffect } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSafeNavigate, useUnsavedChangesContext } from '../hooks/useUnsavedChangesWarning';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { bibliotequesAPI } from '../services/api';

function ModifierBibliothequePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const { allowNextNavigation, setHasUnsavedChanges: setGlobalHasUnsavedChanges } = useUnsavedChangesContext();

  // Détecter si c'est une création ou une modification
  const isCreating = !id || id === 'ajouter';

  // État pour détecter les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { control, handleSubmit, formState: { errors, isDirty }, reset } = useForm({
    defaultValues: {
      nom: '',
      piece: '',
      adresse: '',
      description: ''
    }
  });

  // Récupérer les données de la bibliothèque (seulement en mode modification)
  const { data: bibliotheque, isLoading, error } = useQuery(
    ['bibliotheque', id],
    () => bibliotequesAPI.getById(id).then(res => res.data.data),
    {
      enabled: !isCreating && !!id
    }
  );

  // Surveiller les modifications du formulaire
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
    setGlobalHasUnsavedChanges(isDirty, t(isCreating ? 'ajouterBibliothequePage.unsavedChangesMessage' : 'modifierBibliothequePage.unsavedChangesMessage'));
  }, [isDirty, setGlobalHasUnsavedChanges, isCreating, t]);

  // Pré-remplir le formulaire quand les données sont disponibles (mode modification)
  useEffect(() => {
    if (bibliotheque && !isCreating) {
      reset({
        nom: bibliotheque.nom || '',
        piece: bibliotheque.piece || '',
        adresse: bibliotheque.adresse || '',
        description: bibliotheque.description || ''
      });
    } else if (isCreating) {
      reset({
        nom: '',
        piece: '',
        adresse: '',
        description: ''
      });
    }
  }, [bibliotheque, reset, isCreating]);

  // Mutation pour créer ou mettre à jour la bibliothèque
  const saveBibliothequeMutation = useMutation(
    (data) => {
      // Nettoyer les champs vides
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== '')
      );
      return isCreating
        ? bibliotequesAPI.create(cleanData)
        : bibliotequesAPI.update(id, cleanData);
    },
    {
      onSuccess: (response) => {
        // Réinitialiser les modifications non sauvegardées et autoriser la navigation
        allowNextNavigation();
        setGlobalHasUnsavedChanges(false);
        
        if (isCreating) {
          toast.success(t('ajouterBibliothequePage.createSuccess'));
          queryClient.invalidateQueries('bibliotheques');
          queryClient.invalidateQueries('bibliotheques-list');
          navigate(`/bibliotheques/${response.data.data.id}`);
        } else {
          toast.success(t('modifierBibliothequePage.updateSuccess'));
          queryClient.invalidateQueries(['bibliotheque', id]);
          queryClient.invalidateQueries('bibliotheques');
          queryClient.invalidateQueries('bibliotheques-list');
          navigate(`/bibliotheques/${id}`);
        }
      },
      onError: (error) => {
        console.error(`Erreur lors de la ${isCreating ? 'création' : 'modification'}:`, error);
        
        if (error.response?.status === 409) {
          toast.error(isCreating ? t('ajouterBibliothequePage.alreadyExists') : t('modifierBibliothequePage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(isCreating ? t('ajouterBibliothequePage.createError') : t('modifierBibliothequePage.updateError'));
        }
      }
    }
  );

  const onSubmit = (data) => {
    saveBibliothequeMutation.mutate(data);
  };

  const handleBack = () => {
    navigate(isCreating ? '/bibliotheques' : `/bibliotheques/${id}`);
  };

  if (!isCreating && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isCreating && error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('modifierBibliothequePage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  if (!isCreating && !bibliotheque) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('modifierBibliothequePage.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header avec navigation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 500,
              }}
            >
              {isCreating ? t('ajouterBibliothequePage.title') : t('modifierBibliothequePage.title')}
            </Typography>
            <Button
              data-testid="add-library-save"
              type="submit"
              variant="contained"
              startIcon={saveBibliothequeMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saveBibliothequeMutation.isLoading}
              sx={{ 
                ml: 2,
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
              }}
            >
              {saveBibliothequeMutation.isLoading ? t('modifierBibliothequePage.saving') : t('modifierBibliothequePage.save')}
            </Button>
          </Box>
        </Box>

        {/* Formulaire */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="nom"
              control={control}
              rules={{ 
                required: t(isCreating ? 'ajouterBibliothequePage.nameRequired' : 'modifierBibliothequePage.nameRequired'),
                minLength: { value: 1, message: t(isCreating ? 'ajouterBibliothequePage.nameMinLength' : 'modifierBibliothequePage.nameMinLength') }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t(isCreating ? 'ajouterBibliothequePage.nameLabel' : 'modifierBibliothequePage.nameLabel')}
                  margin="normal"
                  error={!!errors.nom}
                  helperText={errors.nom?.message}
                  inputProps={{ 'data-testid': "add-library-name" }}
                  disabled={saveBibliothequeMutation.isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="piece"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t(isCreating ? 'ajouterBibliothequePage.roomLabel' : 'modifierBibliothequePage.roomLabel')}
                  margin="normal"
                  disabled={saveBibliothequeMutation.isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="adresse"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t(isCreating ? 'ajouterBibliothequePage.addressLabel' : 'modifierBibliothequePage.addressLabel')}
                  margin="normal"
                  disabled={saveBibliothequeMutation.isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t(isCreating ? 'ajouterBibliothequePage.descriptionLabel' : 'modifierBibliothequePage.descriptionLabel')}
                  margin="normal"
                  multiline
                  rows={3}
                  inputProps={{ 'data-testid': "add-library-description" }}
                  disabled={saveBibliothequeMutation.isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={t(isCreating ? 'ajouterBibliothequePage.unsavedChangesMessage' : 'modifierBibliothequePage.unsavedChangesMessage')}
      />
    </Box>
  );
}

export default ModifierBibliothequePage;

